// French catalogue for RegisterPage — one entry for every key the page wraps.
// Keys ARE the English strings (see src/shared/i18n.ts); {placeholders} stay identical.
import { registerFr } from '../i18n/index.js';

registerFr({
  // ── vocabularies: types, weights, statuses (label maps wrapped at render sites) ──
  'Functional': 'Fonctionnel',
  'Non-functional': 'Non fonctionnel',
  'Constraint': 'Contrainte',
  'Assumption': 'Hypothèse',
  'Exclusion': 'Exclusion',
  'functional': 'fonctionnel',
  'non-functional': 'non fonctionnel',
  'constraint': 'contrainte',
  'assumption': 'hypothèse',
  'exclusion': 'exclusion',
  'Must': 'Doit',
  'Should': 'Devrait',
  'Could': 'Pourrait',
  "Won't": 'Exclu',
  'must': 'doit',
  'should': 'devrait',
  'could': 'pourrait',
  "won't": 'exclu',
  'Draft': 'Brouillon',
  'Confirmed': 'Confirmé',
  'Baselined': 'Dans la baseline',
  'Changed': 'Modifié',
  'Delivered': 'Livré',
  'Accepted': 'Accepté',
  'Out of scope': 'Hors périmètre',
  'Planned': 'Planifié',
  'In progress': 'En cours',
  'Ready': 'Prêt',
  'Dropped': 'Abandonné',
  'draft': 'Brouillon',
  'confirmed': 'confirmé',
  'baselined': 'dans la baseline',
  'changed': 'modifié',
  'delivered': 'livré',
  'accepted': 'accepté',
  'out of scope': 'hors périmètre',
  'planned': 'planifié',
  'in progress': 'en cours',
  'ready': 'prêt',
  'dropped': 'abandonné',
  'proposed': 'proposé',

  // ── the five ledger cells and the tabs ──
  'SCOPE': 'PÉRIMÈTRE',
  'HELD': 'RETENU',
  'FUNDED': 'FINANCÉ',
  'PROVEN': 'PROUVÉ',
  'SOURCED': 'SOURCÉ',
  'Client call': 'Entretien client',

  // ── the flow-ownership tooltips on the read-only status chip ──
  'Set by the baseline machinery when the SOW is signed — altered only through a change request.': 'Défini par le mécanisme de baseline lorsque le descriptif des prestations (SOW) est signé — modifié uniquement par une demande de modification.',
  'Set when a change request is approved.': 'Défini lorsqu’une demande de modification est approuvée.',
  'Set by descope (pre-baseline) or an approved change request.': 'Défini par un retrait du périmètre (avant la baseline) ou une demande de modification approuvée.',
  'Set by the delivery flow on the Acceptance page.': 'Défini par le flux de livraison sur la page Acceptation.',
  'Set by the acceptance flow when the client signs off.': 'Défini par le flux d’acceptation lorsque le client donne son accord.',

  // ── deletable entities and the retire-instead hints ──
  'requirement': 'exigence',
  'deliverable': 'livrable',
  'acceptance criterion': 'critère d’acceptation',
  'test case': 'cas de test',
  'Mark out of scope keeps the requirement with every revision and every piece of evidence.': '«Marquer hors périmètre» conserve l’exigence avec chaque révision et chaque preuve.',
  'Setting the status to Dropped keeps the deliverable and its mappings.': 'Le statut «Abandonné» conserve le livrable et ses associations.',
  'Setting the criterion to dropped keeps it and its test-case links.': 'Mettre le critère à «abandonné» le conserve, avec ses liens vers les cas de test.',
  'Setting the test case to Dropped keeps it and any runs the client recorded.': 'Mettre le cas de test à «Abandonné» le conserve, avec toutes les exécutions enregistrées par le client.',

  // ── field words (the "{ref} {field} set to {label}" toast) ──
  'priority': 'poids',
  'type': 'type',
  'status': 'statut',
  'category': 'catégorie',

  // ── revision sources (source.replace('_', ' ')) and CR statuses ──
  'manual': 'manuel',
  'extraction': 'extraction',
  'change request': 'demande de modification',
  'merge': 'fusion',
  'split': 'scission',
  'submitted': 'soumis',
  'approved': 'approuvé',
  'rejected': 'rejeté',
  'deferred': 'reporté',
  'withdrawn': 'retiré',

  // ── open-question words ──
  'open': 'ouvert',
  'settled': 'tranché',
  'answered': 'répondu',
  'refused': 'refusé',
  'agent': 'agent',
  'operator': 'opérateur',
  'portal': 'portail',

  // ── literal keys, in source order of the extracted inventory ──
  ' · filtered': ' · filtrées',
  '— no milestone —': '— aucun jalon —',
  '— no tag on any row': '— aucun tag sur aucune ligne',
  '— none defined yet —': '— aucun défini pour l’instant —',
  '— not defined': '— non défini',
  '— position {side} is what it already says, so the other side is the one that would change it.': '— la position {side} est ce qui est déjà écrit, donc l’autre côté est celui qui le changerait.',
  '— the register itself': '— le registre lui-même',
  '— the row on the register today': '— la ligne telle qu’elle est aujourd’hui dans le registre',
  ', {n} already carried it': ', {n} le portaient déjà',
  '{deleted} deleted, {refused} refused — see the list': '{deleted} supprimée(s), {refused} refusée(s) — voir la liste',
  '{done} held, {refused} refused — {ref}: {reason}': '{done} retenue(s), {refused} refusée(s) — {ref} : {reason}',
  '{done} tagged, {refused} refused — {reason} · {cost}': '{done} taguée(s), {refused} refusée(s) — {reason} · {cost}',
  '{drafts} still proposed ◇': '{drafts} encore proposée(s) ◇',
  '{drafts} still proposed ◇ · {talking} in discussion': '{drafts} encore proposée(s) ◇ · {talking} en discussion',
  '{i} of {n} {bucket}': '{i} sur {n} {bucket}',
  '{musts} must · {exclusions} · {descoped} descoped': '{musts} Doit · {exclusions} · {descoped} retirées du périmètre',
  '{n} cannot say why they say this (P2)': '{n} ne peuvent pas dire pourquoi elles disent cela (P2)',
  '{n} not visible to the client': '{n} non visible(s) pour le client',
  '{n} set to {status}, {skipped} skipped': '{n} définie(s) sur {status}, {skipped} ignorée(s)',
  '{n} unfunded — blocks the estimate': '{n} non financée(s) — bloque l’estimation',
  '{ref} — span in context': '{ref} — extrait source en contexte',
  '{ref} (draft)': '{ref} (brouillon)',
  '{ref} {field} set to {label}': '{ref} : {field} défini sur {label}',
  '{ref} amended → rev {n} — {stamp}': '{ref} adapté → Rév. {n} — {stamp}',
  '{ref} cannot answer “why does it say this?”': '{ref} ne peut pas répondre à «pourquoi est-ce écrit ici ?»',
  '{ref} cannot be deleted': '{ref} ne peut pas être supprimé',
  '{ref} cites nothing — it cannot answer "why does it say this?"': '{ref} ne cite rien — il ne peut pas répondre à «pourquoi est-ce écrit ici ?»',
  '{ref} confirmed': '{ref} confirmé',
  '{ref} confirmed — {stamp}': '{ref} confirmé — {stamp}',
  '{ref} created': '{ref} créé',
  '{ref} created — linter flagged it': '{ref} créé — le linter l’a signalé',
  '{ref} deleted': '{ref} supprimé',
  '{ref} detached from its milestone': '{ref} détaché de son jalon',
  '{ref} goes out of scope and the refusal is recorded, stamped “{stamp}”': '{ref} passe hors périmètre et le refus est consigné, estampillé «{stamp}»',
  '{ref} is {status}': '{ref} est {status}',
  '{ref} is {status} and belongs to a signed baseline — raise a change request to alter it.': '{ref} est {status} et appartient à une baseline signée — soulevez une demande de modification pour le modifier.',
  '{ref} is back in scope as proposed — confirm it once it is agreed': '{ref} est de retour dans le périmètre en tant que proposé — confirmez-le une fois que c’est convenu',
  '{ref} is cited without a stored span — open the row for the full record.': '{ref} est cité sans extrait source enregistré — ouvrez la ligne pour l’enregistrement complet.',
  '{ref} is held — click to change that': '{ref} est retenu — cliquez pour changer cela',
  '{ref} is not in this register — it may have been merged away or deleted.': '{ref} n’est pas dans ce registre — il a peut-être été fusionné ou supprimé.',
  '{ref} is not in this register — nothing on this page carries that ref.': '{ref} n’est pas dans ce registre — rien sur cette page ne porte cette référence.',
  '{ref} is out of scope': '{ref} est hors périmètre',
  '{ref} is out of scope — click to change that': '{ref} est hors périmètre — cliquez pour changer cela',
  '{ref} is proposed again — confirm it once the client has agreed': '{ref} est à nouveau proposé — confirmez-le une fois que le client a donné son accord',
  '{ref} is waiting on the call — decide it there, or confirm it here regardless': '{ref} attend l’entretien — décidez-le là, ou confirmez-le ici tout de même',
  '{ref} moved to that milestone': '{ref} déplacé vers ce jalon',
  '{ref} now at rev {n}': '{ref} désormais à Rév. {n}',
  '{ref} refused': '{ref} refusé',
  '{ref} refused — {row} out of scope': '{ref} refusé — {row} hors périmètre',
  '{ref} rev {n}': '{ref} Rév. {n}',
  '{ref} split into {parts}': '{ref} scindé en {parts}',
  '{ref} stands as written — confirms it and settles the question, stamped “{stamp}”': '{ref} reste tel quel — le confirme et tranche la question, estampillé «{stamp}»',
  '{ref} tags updated': '{ref} : tags mis à jour',
  '{ref}’s evidence is carried onto the target as a superseding revision, and {ref} is marked out of scope. Nothing is deleted.': 'Les preuves de {ref} sont reportées sur la cible comme révision remplaçante, et {ref} est marqué hors périmètre. Rien n’est supprimé.',
  '{source} merged into {target}': '{source} fusionné dans {target}',
  '{subject} is held — an operator stands behind it as scope.': '{subject} est retenue — un opérateur s’en porte garant en tant que périmètre.',
  '{subject} is proposed — nobody has confirmed it yet.': '{subject} est proposée — personne ne l’a encore confirmée.',
  '{updated} updated, {refused} refused — {ref} {reason}': '{updated} mise(s) à jour, {refused} refusée(s) — {ref} {reason}',
  'a new revision, confirms it, and settles this question — stamped “{stamp}”.': 'une nouvelle révision, la confirme et tranche cette question — estampillé «{stamp}».',
  'a new revision. The losing side stays on the record, in the revision it replaces.': 'une nouvelle révision. Le côté perdant reste consigné, dans la révision qu’il remplace.',
  'A pointer, never the data itself': 'Un pointeur, jamais la donnée elle-même',
  'A register belongs to one engagement. Pick one from the switcher to open its requirements.': 'Un registre appartient à un seul mandat. Choisissez-en un dans le sélecteur pour ouvrir ses exigences.',
  'A register is built two ways: the agent proposes from the documents already ingested, or you write the first requirement by hand. Neither is undone — a rejected proposal keeps its reason.': 'Un registre se construit de deux façons : l’agent propose à partir des documents déjà ingérés, ou vous écrivez la première exigence à la main. Aucune des deux n’est annulée — une proposition refusée conserve sa justification.',
  'A test case attaches to a criterion — there is none yet': 'Un cas de test s’attache à un critère — il n’y en a encore aucun',
  'A test case attaches to a criterion, and there is none yet.': 'Un cas de test s’attache à un critère, et il n’y en a encore aucun.',
  'acceptance criteria are unproven. A test case is what turns a criterion into something the client can pass or fail at UAT.': 'les critères d’acceptation ne sont pas prouvés. Un cas de test est ce qui transforme un critère en quelque chose que le client peut réussir ou échouer en UAT.',
  'Acceptance criteria covered': 'Critères d’acceptation couverts',
  'ACs': 'CA',
  'Action': 'Action',
  'Add acceptance criterion': 'Ajouter un critère d’acceptation',
  'Add criterion': 'Ajouter un critère',
  'Add evidence': 'Ajouter une preuve',
  'Add one tag to every selected row': 'Ajouter un tag à chaque ligne sélectionnée',
  'Add part': 'Ajouter une partie',
  'Add step': 'Ajouter une étape',
  'Add tag': 'Ajouter un tag',
  'Add tag…': 'Ajouter un tag…',
  'Add the typed tag': 'Ajouter le tag saisi',
  'add your own tag…': 'ajouter votre propre tag…',
  'All': 'Tous',
  'All categories ({n})': 'Toutes les catégories ({n})',
  'All rows': 'Toutes les lignes',
  'Already in the pool.': 'Déjà dans le pool.',
  'Amend, then confirm': 'Adapter, puis confirmer',
  'and it drops out of the default register view. Nothing is deleted (P4).': 'et elle sort de la vue par défaut du registre. Rien n’est supprimé (P4).',
  'answered: “{error}”.': 'a répondu : «{error}».',
  'asked by the client': 'posée par le client',
  'Back to draft': 'Retour au brouillon',
  'Back to proposed ◇': 'Retour à proposé ◇',
  'Back to proposed ◇ — ask the client first': 'Retour à proposé ◇ — demandez d’abord au client',
  'background': 'contexte',
  'baselined · signed': 'dans la baseline · signé',
  'Bring it back as proposed ◇ — it is confirmed again like any other row': 'La ramener comme proposée ◇ — elle est confirmée à nouveau comme toute autre ligne',
  'Call on {date}': 'Entretien du {date}',
  'Cancel': 'Annuler',
  'Cannot be confirmed: it cites no evidence (P2). Revise it with a quoted span first.': 'Ne peut pas être confirmé : elle ne cite aucune preuve (P2). Révisez-la d’abord avec un extrait source cité.',
  'Category': 'Catégorie',
  'Category of {ref}': 'Catégorie de {ref}',
  'Change requests': 'Demandes de modification',
  'Change the (universal) category': 'Changer la catégorie (universelle)',
  'Change the requirement type': 'Changer le type d’exigence',
  'Change the weight — MoSCoW is editable until the baseline locks it': 'Changer le poids — MoSCoW est modifiable jusqu’à ce que la baseline le verrouille',
  'chars {start}–{end}': 'caractères {start}–{end}',
  'Classification': 'Classification',
  'Clear': 'Réinitialiser',
  'Clear every filter': 'Réinitialiser tous les filtres',
  'Clear pool': 'Vider le pool',
  'Close': 'Fermer',
  'confirm': 'confirmer',
  'Confirm': 'Confirmer',
  'Confirm — stand behind it': 'Confirmer — s’en porter garant',
  'Confirm {ref} — stand behind it as real scope': 'Confirmer {ref} — s’en porter garant comme périmètre réel',
  'Confirmed — as it stands': 'Confirmé — tel quel',
  'Confirming it is refused (P2).': 'La confirmation est refusée (P2).',
  'Cover': 'Couverture',
  'Coverage did not answer.': 'La couverture n’a pas répondu.',
  'Covers': 'Couvre',
  'Covers acceptance criteria': 'Couvre des critères d’acceptation',
  'Create': 'Créer',
  'Create requirement': 'Créer une exigence',
  'Create test case': 'Créer un cas de test',
  'Create the draft row': 'Créer la ligne brouillon',
  'Criteria': 'Critères',
  'current': 'actuelle',
  'D A T': 'L A T',
  'Decide an open question on the call and it lands here with its stamp.': 'Décidez une question ouverte lors de l’entretien, et elle atterrit ici avec son estampille.',
  'Definition of done': 'Critères d’acceptation',
  'Delete {n} permanently': 'Supprimer {n} définitivement',
  'Delete {noun}': 'Supprimer {noun}',
  'Delete {ref}': 'Supprimer {ref}',
  'Delete {ref}?': 'Supprimer {ref} ?',
  'Delete finished': 'Suppression terminée',
  'Delete is for a mistake — a duplicate, a typo, a row that should never have existed.': 'Supprimer est pour une erreur — un doublon, une faute de frappe, une ligne qui n’aurait jamais dû exister.',
  'Delete permanently…': 'Supprimer définitivement…',
  'Deleted': 'Supprimé',
  'Deleting…': 'Suppression…',
  'Deliverable': 'Livrable',
  'Deliverable created': 'Livrable créé',
  'Deliverable linked': 'Livrable lié',
  'Deliverable unlinked': 'Livrable dissocié',
  'deliverables': 'livrables',
  'Deliverables': 'Livrables',
  'Deliverables turn requirements into funded work. Every must-have needs one before G2.': 'Les livrables transforment les exigences en travail financé. Chaque Doit a besoin d’un avant G2.',
  'derived, not stored': 'dérivé, non stocké',
  'descoped': 'retiré du périmètre',
  'Descoped rows — kept with every revision and every piece of evidence (P4)': 'Lignes retirées du périmètre — conservées avec chaque révision et chaque preuve (P4)',
  'Description': 'Description',
  'Do this…': 'Faites ceci…',
  'Document list unavailable ({error}). The requirement can be created without evidence and cited later.': 'Liste de documents indisponible ({error}). L’exigence peut être créée sans preuve et citée plus tard.',
  'Draft ↔ held — baselined, changed, out-of-scope, delivered and accepted are set by their own flows': 'Brouillon ↔ retenu — dans la baseline, modifié, hors périmètre, livré et accepté sont définis par leurs propres flux',
  'Drop': 'Abandonner',
  'e.g. data-residency': 'p. ex. data-residency',
  'Each part becomes a new requirement with its own REQ ref, inheriting {ref}’s evidence, type, priority and category. {ref} is marked out of scope with the split recorded.': 'Chaque partie devient une nouvelle exigence avec sa propre référence REQ, héritant des preuves, du type, du poids et de la catégorie de {ref}. {ref} est marqué hors périmètre, la scission étant consignée.',
  'Every acceptance criterion needs at least one test case before G3.': 'Chaque critère d’acceptation a besoin d’au moins un cas de test avant G3.',
  'Every chip above carries the count it would leave you with, so this state is unreachable by clicking. It is reachable by typing: the search runs on the server and cannot be predicted here.': 'Chaque puce ci-dessus porte le nombre qu’elle vous laisserait, donc cet état est inatteignable en cliquant. Il est atteignable en tapant : la recherche s’exécute sur le serveur et ne peut pas être prédite ici.',
  'Every chip carries its own count, so this should not have been reachable — the rows may have changed under the filter since it was set.': 'Chaque puce porte son propre nombre, cet état n’aurait donc pas dû être atteignable — les lignes ont peut-être changé sous le filtre depuis qu’il a été défini.',
  'every condition this page could compute is met — {list} could not be computed': 'chaque condition que cette page a pu calculer est remplie — {list} n’a pas pu être calculé',
  'every criterion has a test case': 'chaque critère a un cas de test',
  'every criterion is testable': 'chaque critère est testable',
  'Every decision is recorded with this reference — name the call once, e.g. “Call with Mrs Meier · 23.08.2026”': 'Chaque décision est enregistrée avec cette référence — nommez l’entretien une fois, p. ex. «Entretien avec Mme Meier · 23.08.2026»',
  'every must maps to a deliverable': 'chaque Doit est associé à un livrable',
  'every requirement held, funded, proven and sourced': 'chaque exigence retenue, financée, prouvée et sourcée',
  'every row cites a document': 'chaque ligne cite un document',
  'every row stands behind an operator': 'chaque ligne est portée par un opérateur',
  'Every row, descoped included': 'Chaque ligne, y compris celles retirées du périmètre',
  'Every selected row already carries {tag}. Nothing was written.': 'Chaque ligne sélectionnée porte déjà {tag}. Rien n’a été écrit.',
  'Evid': 'Preuve',
  'evidence': 'preuves',
  'Evidence': 'Preuves',
  'Evidence — verbatim spans (P2)': 'Preuves — extraits source littéraux (P2)',
  'Exclusions': 'Exclusions',
  'Expected': 'Attendu',
  'Export ▾': 'Export ▾',
  'Filter by (universal) category': 'Filtrer par catégorie (universelle)',
  'Filter requirements…': 'Filtrer les exigences…',
  'Find the target requirement…': 'Trouver l’exigence cible…',
  'Free-text operator tag — not restricted to the pool. Enter saves.': 'Tag libre de l’opérateur — non limité au pool. Entrée pour enregistrer.',
  'funded by {list}': 'financé par {list}',
  'Given …, when …, then … (include a measurable threshold)': 'Étant donné …, quand …, alors … (incluez un seuil mesurable)',
  'Hinges on': 'Dépend de',
  'History': 'Historique',
  'Hold a claim “· and put to the client”, or let the agent flag a question, and it lands here for the next call.': 'Retenez une déclaration «· et soumettre au client», ou laissez l’agent signaler une question, et elle atterrit ici pour le prochain entretien.',
  'Hold all {n}': 'Retenir les {n}',
  'Hold all {n} proposed criteria — an operator stands behind them as what the client will be held to': 'Retenir les {n} critères proposés — un opérateur s’en porte garant comme ce à quoi le client sera tenu',
  'in discussion': 'en discussion',
  'In discussion': 'En discussion',
  'in play': 'en jeu',
  'Include the descoped': 'Inclure les retirées du périmètre',
  'It': 'L’élément',
  'Lint clean': 'Lint propre',
  'List': 'Liste',
  'Listed in the client’s UAT view.': 'Listé dans la vue UAT du client.',
  'Map': 'Associer',
  'Map the selected requirements to a deliverable': 'Associer les exigences sélectionnées à un livrable',
  'Map to a deliverable…': 'Associer à un livrable…',
  'Map to deliverable…': 'Associer au livrable…',
  'Mapped requirements': 'Exigences associées',
  'Mark {ref} out of scope?': 'Marquer {ref} hors périmètre ?',
  'Mark out of scope': 'Marquer hors périmètre',
  'Mark out of scope…': 'Marquer hors périmètre…',
  'Mark ready': 'Marquer comme prêt',
  'Measured by:': 'Mesuré par :',
  'Measurement method (required for non-functional)': 'Méthode de mesure (requise pour non fonctionnel)',
  'Merge — {ref} becomes out of scope': 'Fusionner — {ref} passe hors périmètre',
  'Merge {ref} into…': 'Fusionner {ref} dans…',
  'Merge into…': 'Fusionner dans…',
  'merged → {ref}': 'fusionné → {ref}',
  'Merged — its content lives on in {ref}, where the evidence was carried over.': 'Fusionné — son contenu vit dans {ref}, où les preuves ont été reportées.',
  'merged into {ref} →': 'fusionné dans {ref} →',
  'Merged statement (the target’s new revision)': 'Déclaration fusionnée (la nouvelle révision de la cible)',
  'Milestone': 'Jalon',
  'More ▾': 'Plus ▾',
  'Name': 'Nom',
  'Neither side yet — it stays on the client-call agenda, and nothing is written.': 'Aucun des deux côtés encore — cela reste à l’agenda de l’entretien client, et rien n’est écrit.',
  'New deliverable': 'Nouveau livrable',
  'New requirement': 'Nouvelle exigence',
  'New test case': 'Nouveau cas de test',
  'No AC linked': 'Aucun CA lié',
  'No acceptance criteria yet.': 'Aucun critère d’acceptation pour l’instant.',
  'no acceptance criterion — nothing to test against': 'aucun critère d’acceptation — rien à quoi tester',
  'no acceptance criterion on record': 'aucun critère d’acceptation consigné',
  'No criterion': 'Aucun critère',
  'No deliverable': 'Aucun livrable',
  'no deliverable — unfunded work': 'aucun livrable — travail non financé',
  'No deliverable to map to yet': 'Aucun livrable à associer pour l’instant',
  'No deliverables yet': 'Aucun livrable pour l’instant',
  'No eligible target.': 'Aucune cible éligible.',
  'No engagement selected': 'Aucun mandat sélectionné',
  'No engagement selected.': 'Aucun mandat sélectionné.',
  'No evidence': 'Aucune preuve',
  'No milestones yet — define them on Contracting → Payment schedule': 'Aucun jalon pour l’instant — définissez-les dans Contractualisation → Échéancier de paiement',
  'no MUST requirement on record': 'aucune exigence DOIT consignée',
  'No requirement — unjustified work (blocks G2)': 'Aucune exigence — travail injustifié (bloque G2)',
  'No requirement matches that filter.': 'Aucune exigence ne correspond à ce filtre.',
  'no response': 'aucune réponse',
  'no row fits — this side becomes a new draft requirement (functional · should)': 'aucune ligne ne convient — ce côté devient une nouvelle exigence brouillon (fonctionnel · Devrait)',
  'No row matches': 'Aucune ligne ne correspond',
  'No row on the register carries this claim, so it enters as a new DRAFT requirement (functional · should). It will cite no evidence, so confirming it is refused until you revise it with a span.': 'Aucune ligne du registre ne porte cette déclaration, elle entre donc comme nouvelle exigence BROUILLON (fonctionnel · Devrait). Elle ne citera aucune preuve, la confirmation sera donc refusée tant que vous ne la révisez pas avec un extrait source.',
  'No tag exists yet — set the pool first': 'Aucun tag n’existe encore — définissez d’abord le pool',
  'No tags — the agent will propose none.': 'Aucun tag — l’agent n’en proposera aucun.',
  'No test case': 'Aucun cas de test',
  'no test case — the client has nothing to run at UAT': 'aucun cas de test — le client n’a rien à exécuter en UAT',
  'No test case exists in {code}': 'Aucun cas de test n’existe dans {code}',
  'No test cases': 'Aucun cas de test',
  'none': 'aucun',
  'None linked.': 'Aucun lié.',
  'not applicable': 'non applicable',
  'not applicable — an exclusion funds nothing': 'non applicable — une exclusion ne finance rien',
  'not applicable to an exclusion': 'non applicable à une exclusion',
  'Not defined.': 'Non défini.',
  'Not visible to the client until it is ready.': 'Non visible pour le client tant qu’il n’est pas prêt.',
  'nothing {bucket}': 'rien n’est {bucket}',
  'Nothing else points at it.': 'Rien d’autre ne s’y réfère.',
  'nothing here is testable yet': 'rien n’est testable ici pour l’instant',
  'Nothing in this selection can be deleted.': 'Rien dans cette sélection ne peut être supprimé.',
  'Nothing is cited on the current revision —': 'Rien n’est cité sur la révision actuelle —',
  'Nothing is on record for {code}': 'Rien n’est consigné pour {code}',
  'Nothing is outstanding': 'Rien n’est en suspens',
  'nothing on record yet': 'rien n’est encore consigné',
  'nothing open — the agenda is clear': 'rien n’est ouvert — l’agenda est vide',
  'nothing settled yet': 'rien n’est encore tranché',
  'Nothing settled yet.': 'Rien n’est encore tranché.',
  'Nothing that could be checked is outstanding': 'Rien de ce qui pouvait être vérifié n’est en suspens',
  'Obligations': 'Obligations',
  'Observe that…': 'Observez que…',
  'of': 'sur',
  'of {n}': 'sur {n}',
  'of {n} criteria': 'sur {n} critères',
  'of {n} must': 'sur {n} Doit',
  'On the client-call agenda — open the call at this question': 'À l’agenda de l’entretien client — ouvrir l’entretien à cette question',
  'Only rows citing': 'Seulement les lignes citant',
  'Only rows tagged {tag}': 'Seulement les lignes taguées {tag}',
  'Open {ref}': 'Ouvrir {ref}',
  'Open {ref} →': 'Ouvrir {ref} →',
  'Open {ref} at its evidence': 'Ouvrir {ref} à ses preuves',
  'Open the row': 'Ouvrir la ligne',
  'Open the row — revise, merge, split': 'Ouvrir la ligne — réviser, fusionner, scinder',
  'Opposes': 'S’oppose à',
  '…or none fits — make it a new row': '…ou aucune ne convient — faites-en une nouvelle ligne',
  'out of scope — CR only': 'hors périmètre — uniquement par demande de modification',
  'Overall expected result': 'Résultat global attendu',
  'Part {n}': 'Partie {n}',
  'Paste the exact sentence from the document — it is validated as a literal substring.': 'Collez la phrase exacte du document — elle est validée comme sous-chaîne littérale.',
  'Pick from the scheme…': 'Choisir dans le schéma…',
  'Position {side}': 'Position {side}',
  'Position {side} wins →': 'La position {side} l’emporte →',
  'Position {side} wins — {ref}': 'La position {side} l’emporte — {ref}',
  'Preconditions': 'Conditions préalables',
  'Priority': 'Priorité',
  'proposal #{id}': 'proposition n° {id}',
  'Proposed ◇': 'Proposé ◇',
  'question': 'question',
  'Question': 'Question',
  'Question dropped': 'Question abandonnée',
  'Raise a change request →': 'Soulever une demande de modification →',
  'Raised by the agent — nobody has answered it': 'Posée par l’agent — personne n’y a répondu',
  'raised by the operator': 'posée par l’opérateur',
  'rather than printing a confident zero.': 'plutôt que d’afficher un zéro avec assurance.',
  'Rationale': 'Justification',
  'ready for the client': 'prêts pour le client',
  'Record resolution': 'Consigner la décision',
  'Record something else →': 'Consigner autre chose →',
  'Record the answer →': 'Consigner la réponse →',
  'Record what was decided or done — stamped “{stamp}”': 'Consigner ce qui a été décidé ou fait — estampillé «{stamp}»',
  'Ref': 'Réf',
  'Refine this requirement with the client': 'Affiner cette exigence avec le client',
  'Refused': 'Refusé',
  'Refused — out of scope': 'Refusé — hors périmètre',
  'Register CSV': 'CSV du registre',
  'Remove {tag} from {ref}': 'Retirer {tag} de {ref}',
  'Remove {tag} from the pool': 'Retirer {tag} du pool',
  'Remove evidence row': 'Retirer la ligne de preuve',
  'Remove part': 'Retirer la partie',
  'Remove step': 'Retirer l’étape',
  'Remove tag {tag}': 'Retirer le tag {tag}',
  'Remove the pool — the agent proposes no tags at all': 'Retirer le pool — l’agent ne propose aucun tag du tout',
  'Requirement': 'Exigence',
  'Requirements': 'Exigences',
  'Requirements register': 'Registre des exigences',
  'Requirements this funds': 'Exigences que cela finance',
  'Resolution recorded — the agent reads it as a client decision on its next run': 'Décision consignée — l’agent la lit comme une décision du client lors de sa prochaine exécution',
  'Restore to scope — proposed ◇': 'Rétablir dans le périmètre — proposé ◇',
  'rev {n}': 'Rév. {n}',
  'Revise → new revision': 'Réviser → nouvelle révision',
  'Revise → rev {n}': 'Réviser → Rév. {n}',
  'Revise {ref} → rev {n}': 'Réviser {ref} → Rév. {n}',
  'revises {ref} with this side · the losing side stays on the record': 'révise {ref} avec ce côté · le côté perdant reste consigné',
  'Roadmap': 'Roadmap',
  'Rows carrying at least one criterion with no test case. PROVEN above counts CRITERIA, not rows — different units, both labelled.': 'Lignes portant au moins un critère sans cas de test. PROUVÉ ci-dessus compte des CRITÈRES, pas des lignes — unités différentes, toutes deux étiquetées.',
  'Rows in play only — the descoped are hidden, because that is the qualifier the gate tally you arrived from carries.': 'Lignes en jeu uniquement — celles retirées du périmètre sont masquées, car c’est le qualificatif que porte le décompte de gate d’où vous venez.',
  'Rows named by an OPEN question on the client call — decide them on the call, then confirm': 'Lignes désignées par une question OUVERTE lors de l’entretien client — décidez-les lors de l’entretien, puis confirmez',
  'Rows nobody has stood behind yet': 'Lignes dont personne ne s’est encore porté garant',
  'rows shown': 'lignes affichées',
  'Rows weighted {weight}, including any that are out of scope. The SCOPE cell counts only rows in play.': 'Lignes pondérées {weight}, y compris celles hors périmètre. La cellule PÉRIMÈTRE ne compte que les lignes en jeu.',
  'Rows with no acceptance criterion — nothing to test them against': 'Lignes sans critère d’acceptation — rien à quoi les tester',
  'Save mapping ({n})': 'Enregistrer l’association ({n})',
  'Save pool': 'Enregistrer le pool',
  'Save revision': 'Enregistrer la révision',
  'Search statements…': 'Rechercher des déclarations…',
  'searching the statements…': 'recherche dans les déclarations…',
  'Select all': 'Tout sélectionner',
  'Select document…': 'Sélectionner un document…',
  'Select every visible row': 'Sélectionner chaque ligne visible',
  'selected': 'sélectionnées',
  'Set weight…': 'Définir le poids…',
  'Show all {n} revisions': 'Afficher les {n} révisions',
  'Show every document': 'Afficher tous les documents',
  'Show latest 3': 'Afficher les 3 dernières',
  'Show the proposed': 'Afficher les proposées',
  'Show the span {ref} cites in {doc}': 'Afficher l’extrait source que {ref} cite dans {doc}',
  'Show the span behind this question in {ref}': 'Afficher l’extrait source derrière cette question dans {ref}',
  'Show the uncited': 'Afficher les non citées',
  'Show the unfunded': 'Afficher les non financées',
  'signed — CR only': 'signé — uniquement par demande de modification',
  'Signed scope — every change goes through a change request.': 'Périmètre signé — chaque changement passe par une demande de modification.',
  'source': 'source',
  'Source': 'Source',
  'Split…': 'Scinder…',
  'Split {ref}': 'Scinder {ref}',
  'Split into {n} — {ref} becomes out of scope': 'Scinder en {n} — {ref} passe hors périmètre',
  'stamped as': 'estampillé comme',
  'Statement': 'Déclaration',
  'Status': 'Statut',
  'Status of {ref}': 'Statut de {ref}',
  'Steps': 'Étapes',
  'still draft': 'encore en brouillon',
  'superseded document': 'document remplacé',
  'Tag {ref} with {tag}': 'Taguer {ref} avec {tag}',
  'Tag pool': 'Pool de tags',
  'Tag pool — vocabulary editor': 'Pool de tags — éditeur de vocabulaire',
  'Tag pool cleared — the agent proposes no tags': 'Pool de tags vidé — l’agent ne propose aucun tag',
  'Tag pool saved — the agent may tag from it on its next run': 'Pool de tags enregistré — l’agent peut y puiser des tags lors de sa prochaine exécution',
  'Tags': 'Tags',
  'Tags are lowercase-kebab (letters/digits and single hyphens), 2–30 characters.': 'Les tags sont en lowercase-kebab (lettres/chiffres et traits d’union simples), 2 à 30 caractères.',
  'Test case': 'Cas de test',
  'test cases': 'cas de test',
  'Test cases': 'Cas de test',
  'Test data': 'Données de test',
  'Test data reference': 'Référence des données de test',
  'Test-case pass queued — the proposals land in the review queue': 'Passe de cas de test mise en file — les propositions atterrissent dans la file de révision',
  'The {noun} is removed from the register for good.': 'Cet élément — {noun} — est retiré du registre pour de bon.',
  'The agenda is clear.': 'L’agenda est vide.',
  'the agent asks': 'l’agent demande',
  'The agent reads settled questions as client decisions on its next run.': 'L’agent lit les questions tranchées comme des décisions du client lors de sa prochaine exécution.',
  'the client call': 'l’entretien client',
  'The client-call agenda': 'L’agenda de l’entretien client',
  'the coverage roll-up did not answer': 'l’agrégation de couverture n’a pas répondu',
  'The coverage roll-up did not answer — this is not a zero.': 'L’agrégation de couverture n’a pas répondu — ce n’est pas un zéro.',
  'The coverage roll-up did not answer, so this is not a clear verdict — it is the verdict on what answered.': 'L’agrégation de couverture n’a pas répondu, ce n’est donc pas un verdict clair — c’est le verdict sur ce qui a répondu.',
  'The ledger reports what it can count from those rows and marks the rest': 'Le bilan rapporte ce qu’il peut compter à partir de ces lignes et marque le reste',
  'The live app stores this link as proposal.target_requirement_id; a question logged FROM a conflict does not carry it, so the row is matched on the statement.': 'L’application en production stocke ce lien sous proposal.target_requirement_id ; une question consignée DEPUIS un conflit ne le porte pas, la ligne est donc mise en correspondance sur la déclaration.',
  'The milestone this deliverable is invoiced and accepted against': 'Le jalon sur lequel ce livrable est facturé et accepté',
  'The obligation': 'L’obligation',
  'The only tags the agent may attach to its proposals — engagement-specific classification next to the (universal) category. Lowercase-kebab, 2–30 characters, at most 30 tags. Your own tag edits on requirements are not restricted to this pool.': 'Les seuls tags que l’agent peut attacher à ses propositions — classification propre au mandat, à côté de la catégorie (universelle). Lowercase-kebab, 2 à 30 caractères, 30 tags au maximum. Vos propres modifications de tags sur les exigences ne sont pas limitées à ce pool.',
  'The open questions did not load': 'Les questions ouvertes n’ont pas pu être chargées',
  'The register did not answer': 'Le registre n’a pas répondu',
  'The register is empty.': 'Le registre est vide.',
  'The requirement is kept with every revision and every piece of evidence — its status becomes': 'L’exigence est conservée avec chaque révision et chaque preuve — son statut devient',
  'The roll-up said: “{error}”.': 'L’agrégation a répondu : «{error}».',
  'The rows': 'Les lignes',
  'The rows below are the register itself and are unaffected — they come from': 'Les lignes ci-dessous sont le registre lui-même et ne sont pas affectées — elles proviennent de',
  'the search failed: “{error}”': 'la recherche a échoué : «{error}»',
  'The system shall …': 'Le système doit …',
  'The winning statement cannot be empty.': 'La déclaration gagnante ne peut pas être vide.',
  'then': 'ensuite',
  'This edits the VOCABULARY only — nothing here tags a requirement. To tag one, open its detail panel and click the pool chips there.': 'Ceci modifie uniquement le VOCABULAIRE — rien ici ne tague une exigence. Pour en taguer une, ouvrez son panneau de détail et cliquez sur les puces du pool.',
  'This statement': 'Cette déclaration',
  'This writes': 'Ceci écrit sur',
  'Title': 'Titre',
  'Traceability CSV': 'CSV de traçabilité',
  'Try again': 'Réessayer',
  'Type': 'Type',
  'Type of {ref}': 'Type de {ref}',
  'uncategorised': 'sans catégorie',
  'unfunded — no deliverable carries it': 'non financé — aucun livrable ne le porte',
  'unjustified': 'injustifié',
  'Unjustified': 'Injustifié',
  'unknown': 'inconnu',
  'Unlink {ref}': 'Dissocier {ref}',
  'unlinked': 'non liés',
  'Unsaved mapping changes — Save mapping applies them.': 'Modifications d’association non enregistrées — «Enregistrer l’association» les applique.',
  'View in context →': 'Voir en contexte →',
  'Weight': 'Poids',
  'Weight of {ref}': 'Poids de {ref}',
  'What must be true for this to count as delivered.': 'Ce qui doit être vrai pour que cela compte comme livré.',
  'What the contract says is NOT included — first-class scope, not an absence': 'Ce que le contrat dit comme NON inclus — un périmètre à part entière, pas une absence',
  'what the next gate still wants from this register — one clause, then the rest': 'ce que le prochain gate attend encore de ce registre — une clause, puis le reste',
  'Why this exists — the business reason, not the implementation.': 'Pourquoi cela existe — la raison métier, pas l’implémentation.',
  'Will be deleted': 'Sera supprimé',
  'without a milestone': 'sans jalon',
  'Write a test case against these criteria': 'Écrire un cas de test pour ces critères',
  'Write it to {ref}': 'L’écrire dans {ref}',
  'Write one by hand': 'En écrire une à la main',
  'Writes': 'Écrit sur',

  // ── plurals (both forms are keys) ──
  '{n} acceptance criterion': '{n} critère d’acceptation',
  '{n} acceptance criteria': '{n} critères d’acceptation',
  '{n} call — bulk takes tags as a replacement set, so one per resulting tag set': '{n} appel — le traitement en masse prend les tags comme un ensemble de remplacement, donc un par ensemble de tags résultant',
  '{n} calls — bulk takes tags as a replacement set, so one per resulting tag set': '{n} appels — le traitement en masse prend les tags comme un ensemble de remplacement, donc un par ensemble de tags résultant',
  '{n} condition is not on this ladder at all — {list} needs the coverage roll-up, and it did not answer.': '{n} condition ne figure pas du tout sur cette échelle — {list} a besoin de l’agrégation de couverture, et elle n’a pas répondu.',
  '{n} conditions are not on this ladder at all — {list} need the coverage roll-up, and it did not answer.': '{n} conditions ne figurent pas du tout sur cette échelle — {list} ont besoin de l’agrégation de couverture, et elle n’a pas répondu.',
  '{n} criterion': '{n} critère',
  '{n} criteria': '{n} critères',
  '{n} criterion held — an operator now stands behind it': '{n} critère retenu — un opérateur s’en porte désormais garant',
  '{n} criteria held — an operator now stands behind them': '{n} critères retenus — un opérateur s’en porte désormais garant',
  '{n} criterion with no test case': '{n} critère sans cas de test',
  '{n} criteria with no test case': '{n} critères sans cas de test',
  '{n} deliverable': '{n} livrable',
  '{n} deliverables': '{n} livrables',
  '{n} evidence span rejected — a quote must be a literal substring of the cited document. Fix the marked rows and submit again; nothing was saved.': '{n} extrait source rejeté — une citation doit être une sous-chaîne littérale du document cité. Corrigez les lignes marquées et soumettez à nouveau ; rien n’a été enregistré.',
  '{n} evidence spans rejected — a quote must be a literal substring of the cited document. Fix the marked rows and submit again; nothing was saved.': '{n} extraits source rejetés — une citation doit être une sous-chaîne littérale du document cité. Corrigez les lignes marquées et soumettez à nouveau ; rien n’a été enregistré.',
  '{n} evidence span rejected — see the marked rows. Nothing was saved.': '{n} extrait source rejeté — voir les lignes marquées. Rien n’a été enregistré.',
  '{n} evidence spans rejected — see the marked rows. Nothing was saved.': '{n} extraits source rejetés — voir les lignes marquées. Rien n’a été enregistré.',
  '{n} exclusion': '{n} exclusion',
  '{n} exclusions': '{n} exclusions',
  '{n} requirement': '{n} exigence',
  '{n} requirements': '{n} exigences',
  '{n} requirement deleted': '{n} exigence supprimée',
  '{n} requirements deleted': '{n} exigences supprimées',
  '{n} requirement mapped to {ref}': '{n} exigence associée à {ref}',
  '{n} requirements mapped to {ref}': '{n} exigences associées à {ref}',
  '{n} requirement updated': '{n} exigence mise à jour',
  '{n} requirements updated': '{n} exigences mises à jour',
  '{n} revision': '{n} révision',
  '{n} revisions': '{n} révisions',
  '{n} row tagged {tag}{already} · {cost}': '{n} ligne taguée {tag}{already} · {cost}',
  '{n} rows tagged {tag}{already} · {cost}': '{n} lignes taguées {tag}{already} · {cost}',
  '{n} statement matched “{q}” on the server (trigram + ILIKE over current revisions)': '{n} déclaration correspondait à «{q}» sur le serveur (trigramme + ILIKE sur les révisions actuelles)',
  '{n} statements matched “{q}” on the server (trigram + ILIKE over current revisions)': '{n} déclarations correspondaient à «{q}» sur le serveur (trigramme + ILIKE sur les révisions actuelles)',
  '{n} test case': '{n} cas de test',
  '{n} test cases': '{n} cas de test',
  '{n} test case now {status}': '{n} cas de test désormais {status}',
  '{n} test cases now {status}': '{n} cas de test désormais {status}',
  '{ref} maps {n} requirement': '{ref} associe {n} exigence',
  '{ref} maps {n} requirements': '{ref} associe {n} exigences',
  'Back to proposed ◇ — {n} row': 'Retour à proposé ◇ — {n} ligne',
  'Back to proposed ◇ — {n} rows': 'Retour à proposé ◇ — {n} lignes',
  'Delete {n} requirement?': 'Supprimer {n} exigence ?',
  'Delete {n} requirements?': 'Supprimer {n} exigences ?',
  'Delete {n} row permanently…': 'Supprimer {n} ligne définitivement…',
  'Delete {n} rows permanently…': 'Supprimer {n} lignes définitivement…',
  'Propose test cases for {n} criterion': 'Proposer des cas de test pour {n} critère',
  'Propose test cases for {n} criteria': 'Proposer des cas de test pour {n} critères',
  'Run extraction on {n} ready document': 'Exécuter l’extraction sur {n} document prêt',
  'Run extraction on {n} ready documents': 'Exécuter l’extraction sur {n} documents prêts',
  'selected row is no longer in this engagement and was left alone.': 'ligne sélectionnée n’est plus dans ce mandat et n’a pas été touchée.',
  'selected rows are no longer in this engagement and were left alone.': 'lignes sélectionnées ne sont plus dans ce mandat et n’ont pas été touchées.',

  // — merged in from the pencil-layer branch —
  'Adopt the {n} assumption pencilled against this question': 'Adopter {n} hypothèse rattachée à cette question',
  'Adopt the {n} assumptions pencilled against this question': 'Adopter {n} hypothèses rattachées à cette question',
  '{n} assumption adopted': '{n} hypothèse adoptée',
  '{n} assumptions adopted': '{n} hypothèses adoptées',
  '{n} assumption reverted': '{n} hypothèse abandonnée',
  '{n} assumptions reverted': '{n} hypothèses abandonnées',
  '{n} date is pencilled against this question — confirming or refusing rubs it out': '{n} date est esquissée sur cette question — confirmer ou refuser l’efface',
  '{n} dates are pencilled against this question — confirming or refusing rubs them out': '{n} dates sont esquissées sur cette question — confirmer ou refuser les efface',

  // ── the compose overlay's overview rail, the editable detail modal, the REQ hover ──
  'not funded yet': 'pas encore financée',
  'already funded by': 'déjà financée par',
  'No deliverable covers this yet (G2)': 'Aucun livrable ne couvre encore ceci (G2)',
  '{ref} renamed': '{ref} renommé',
  '{ref} description saved': '{ref} description enregistrée',
  '{ref} definition of done saved': '{ref} critères d’acceptation enregistrés',
  // ── the reference system: the # picker, who-names-this, the dangle warning ──
  '# inserts a reference — DEL-xx, REQ-xxx': '# insère une référence — DEL-xx, REQ-xxx',
  'Referenced in': 'Référencé dans',
  'texts that name {ref} — change it and they may need to move along': 'textes qui nomment {ref} — si vous le changez, ils devront peut-être suivre',
  '{n} text elsewhere names {ref} — it stays, and the name will dangle. Update it afterwards.':
    '{n} texte ailleurs nomme {ref} — il reste, et le nom restera en suspens. Mettez-le à jour ensuite.',
  '{n} texts elsewhere name {ref} — they stay, and the name will dangle. Update them afterwards.':
    '{n} textes ailleurs nomment {ref} — ils restent, et le nom restera en suspens. Mettez-les à jour ensuite.',
  '{n} text keeps naming it — update after': '{n} texte le nomme toujours — à mettre à jour ensuite',
  '{n} texts keep naming it — update after': '{n} textes le nomment toujours — à mettre à jour ensuite',
  'ref must be a register ref like DEL-02 or REQ-014.': 'ref doit être une référence du registre comme DEL-02 ou REQ-014.',
}, 'RegisterPage');
